import { useEffect } from 'react'

import { Select } from 'antd'
import { useWatch } from 'antd/es/form/Form'

import type {
  IOctaveAnalysis,
  TFreqOctave,
  TMeasurementForm
} from '@web/entities/measurement'
import { convertFreqOctave } from '@web/entities/measurement/lib/octave.logic'
import {
  averageTypeOptions,
  mapAverageType
} from '@web/entities/measurement/model/constants/average-type.constant'
import {
  frequencyOneOptions as rawFrequencyOneOptions,
  frequencyThirdOptions as rawFrequencyThirdOptions,
  frequencyTwoOptions as rawFrequencyTwoOptions,
  magnitudeTypeOptions,
  octaveTypeOptions
} from '@web/entities/measurement/model/constants/options.constant'
import { EAverageType } from '@web/entities/measurement/model/enums/average-type.enum'
import { EMagnitudeType } from '@web/entities/measurement/model/enums/magnitude-type.enum'
import { EOctaveType } from '@web/entities/measurement/model/enums/octave.enum'
import { AppForm } from '@web/shared/components/app-form'
import { AppInputNumber } from '@web/shared/components/app-input-number'
import { TextView } from '@web/shared/components/text-view'
import { ValidationError } from '@web/shared/constants/validation-errors.constant'
import type { IOption } from '@web/shared/types/options.type'
import { formatRealNumber } from '@web/shared/utils/format-real-number.util'
import { validateMinValue } from '@web/shared/utils/validation-rules.util'

import type { FormInstance } from 'antd'

interface IProps {
  isDraftMode: boolean
  octaveData?: IOctaveAnalysis
  measurementForm: FormInstance<TMeasurementForm>
}

const isOctaveOver5Hz = (octave: IOption<TFreqOctave, unknown>) =>
  Number(octave.label) > 5
const orderOctaveByAsc = (
  a: IOption<TFreqOctave, unknown>,
  b: IOption<TFreqOctave, unknown>
) => Number(a.label) - Number(b.label)
const frequencyOneOptions = rawFrequencyOneOptions.filter(isOctaveOver5Hz)
const frequencyThirdOptions = rawFrequencyThirdOptions.filter(isOctaveOver5Hz)
const frequencyTwoOptions = rawFrequencyTwoOptions.filter(isOctaveOver5Hz)
const octaveRangeError =
  'Верхняя граница частоты должна быть не меньше нижней'

export const MeasurementFormOctave = ({
  isDraftMode,
  octaveData,
  measurementForm
}: IProps) => {
  const octaveTypeWatched = useWatch(
    ['octaveAnalysisDescription', 'octaveType'],
    measurementForm
  )

  // Устанавливаем значения по умолчанию при создании новой формы
  useEffect(() => {
    if (!isDraftMode) return

    measurementForm.setFieldsValue({
      octaveAnalysisDescription: {
        magnitudeType: EMagnitudeType.RMS,
        octaveType: EOctaveType.ONE,
        overlap: 50,
        averageType: EAverageType.LINEAR,
        averageNumber: 1,
        lvl0db: 1e-6
      }
    })
  }, [isDraftMode, measurementForm])

  // Сброс полей частот при изменении типа
  useEffect(() => {
    if (!octaveTypeWatched) return

    measurementForm.resetFields([
      ['octaveAnalysisDescription', 'frequencyLowOctaveOne'],
      ['octaveAnalysisDescription', 'frequencyHighOctaveOne'],
      ['octaveAnalysisDescription', 'frequencyLowOctaveThird'],
      ['octaveAnalysisDescription', 'frequencyHighOctaveThird'],
      ['octaveAnalysisDescription', 'frequencyLowOctaveTwo'],
      ['octaveAnalysisDescription', 'frequencyHighOctaveTwo']
    ])
  }, [measurementForm, octaveTypeWatched])

  const validateHighOctave =
    (lowFieldName: keyof IOctaveAnalysis) =>
    (_: unknown, highOctave?: TFreqOctave) => {
      const lowOctave = measurementForm.getFieldValue([
        'octaveAnalysisDescription',
        lowFieldName
      ]) as TFreqOctave | undefined

      if (!lowOctave || !highOctave) return Promise.resolve()

      const lowFrequency = convertFreqOctave(lowOctave)
      const highFrequency = convertFreqOctave(highOctave)

      if (lowFrequency > highFrequency) {
        return Promise.reject(new Error(octaveRangeError))
      }

      return Promise.resolve()
    }

  const octavaDataStatic = (() => {
    if (isDraftMode) return null
    if (!octaveData) return null

    const base = {
      magnitude: magnitudeTypeOptions.find(
        (o) => o.value === octaveData.magnitudeType
      )?.label,
      octava: octaveTypeOptions.find((o) => o.value === octaveData.octaveType)
        ?.label,
      overlap: octaveData.overlap,
      averageType: mapAverageType[octaveData.averageType],
      averageNumber: octaveData.averageNumber,
      lvl0Db: octaveData.lvl0db,
      lanesCount: octaveData?.lanesCount ?? 0,
      frequencyLow: null,
      frequencyHigh: null
    }

    switch (octaveData.octaveType) {
      case EOctaveType.ONE:
        return {
          ...base,
          frequencyLow: frequencyOneOptions.find(
            (o) => o.value === octaveData.frequencyLowOctaveOne
          )?.label,
          frequencyHigh: frequencyOneOptions.find(
            (o) => o.value === octaveData.frequencyHighOctaveOne
          )?.label
        }

      case EOctaveType.THIRD:
        return {
          ...base,
          frequencyLow: frequencyThirdOptions.find(
            (o) => o.value === octaveData.frequencyLowOctaveThird
          )?.label,
          frequencyHigh: frequencyThirdOptions.find(
            (o) => o.value === octaveData.frequencyHighOctaveThird
          )?.label
        }

      case EOctaveType.TWO:
        return {
          ...base,
          frequencyLow: frequencyTwoOptions.find(
            (o) => o.value === octaveData.frequencyLowOctaveTwo
          )?.label,
          frequencyHigh: frequencyTwoOptions.find(
            (o) => o.value === octaveData.frequencyHighOctaveTwo
          )?.label
        }

      default:
        return base
    }
  })()

  return (
    <>
      {isDraftMode && (
        <>
          <AppForm.Item
            label='Магнитуда'
            name={['octaveAnalysisDescription', 'magnitudeType']}
            rules={[{ required: true, message: ValidationError.Required }]}
          >
            <Select
              options={magnitudeTypeOptions}
              placeholder='Выберите тип магнитуды'
            />
          </AppForm.Item>

          <AppForm.Item
            label='Доля октавы'
            name={['octaveAnalysisDescription', 'octaveType']}
            rules={[{ required: true, message: ValidationError.Required }]}
          >
            <Select
              options={octaveTypeOptions}
              placeholder='Выберите долю октавы'
            />
          </AppForm.Item>

          {octaveTypeWatched === EOctaveType.ONE && (
            <>
              <AppForm.Item
                label='Нижняя граница частоты, Гц'
                name={['octaveAnalysisDescription', 'frequencyLowOctaveOne']}
                rules={[{ required: true, message: ValidationError.Required }]}
              >
                <Select
                  options={[...frequencyOneOptions].sort(orderOctaveByAsc)}
                  placeholder='Выберите нижнюю частоту'
                />
              </AppForm.Item>

              <AppForm.Item
                label='Верхняя граница частоты, Гц'
                name={['octaveAnalysisDescription', 'frequencyHighOctaveOne']}
                dependencies={[
                  ['octaveAnalysisDescription', 'frequencyLowOctaveOne']
                ]}
                rules={[
                  { required: true, message: ValidationError.Required },
                  { validator: validateHighOctave('frequencyLowOctaveOne') }
                ]}
              >
                <Select
                  options={frequencyOneOptions}
                  placeholder='Выберите верхнюю частоту'
                />
              </AppForm.Item>
            </>
          )}

          {octaveTypeWatched === EOctaveType.THIRD && (
            <>
              <AppForm.Item
                label='Нижняя граница частоты, Гц'
                name={['octaveAnalysisDescription', 'frequencyLowOctaveThird']}
                rules={[{ required: true, message: ValidationError.Required }]}
              >
                <Select
                  options={[...frequencyThirdOptions].sort(orderOctaveByAsc)}
                  placeholder='Выберите нижнюю частоту'
                />
              </AppForm.Item>

              <AppForm.Item
                label='Верхняя граница частоты, Гц'
                name={['octaveAnalysisDescription', 'frequencyHighOctaveThird']}
                dependencies={[
                  ['octaveAnalysisDescription', 'frequencyLowOctaveThird']
                ]}
                rules={[
                  { required: true, message: ValidationError.Required },
                  { validator: validateHighOctave('frequencyLowOctaveThird') }
                ]}
              >
                <Select
                  options={frequencyThirdOptions}
                  placeholder='Выберите верхнюю частоту'
                />
              </AppForm.Item>
            </>
          )}

          {octaveTypeWatched === EOctaveType.TWO && (
            <>
              <AppForm.Item
                label='Нижняя граница частоты, Гц'
                name={['octaveAnalysisDescription', 'frequencyLowOctaveTwo']}
                rules={[{ required: true, message: ValidationError.Required }]}
              >
                <Select
                  options={[...frequencyTwoOptions].sort(orderOctaveByAsc)}
                  placeholder='Выберите нижнюю частоту'
                />
              </AppForm.Item>

              <AppForm.Item
                label='Верхняя граница частоты, Гц'
                name={['octaveAnalysisDescription', 'frequencyHighOctaveTwo']}
                dependencies={[
                  ['octaveAnalysisDescription', 'frequencyLowOctaveTwo']
                ]}
                rules={[
                  { required: true, message: ValidationError.Required },
                  { validator: validateHighOctave('frequencyLowOctaveTwo') }
                ]}
              >
                <Select
                  options={frequencyTwoOptions}
                  placeholder='Выберите верхнюю частоту'
                />
              </AppForm.Item>
            </>
          )}

          <AppForm.Item
            label='Перекрытие (%)'
            name={['octaveAnalysisDescription', 'overlap']}
            rules={[{ required: true, message: ValidationError.Required }]}
          >
            <AppInputNumber
              step={1}
              min={0}
              max={100}
              placeholder='Введите процент перекрытия'
            />
          </AppForm.Item>

          <AppForm.Item
            label='Тип усреднения'
            name={['octaveAnalysisDescription', 'averageType']}
            rules={[{ required: true, message: ValidationError.Required }]}
          >
            <Select
              options={averageTypeOptions}
              placeholder='Выберите тип усреднения'
            />
          </AppForm.Item>

          <AppForm.Item
            label='Кол-во усреднений'
            name={['octaveAnalysisDescription', 'averageNumber']}
            rules={[{ required: true, message: ValidationError.Required }]}
          >
            <AppInputNumber min={1} placeholder='Введите число усреднений' />
          </AppForm.Item>
          <AppForm.Item
            label='Уровень 0 дБ'
            name={['octaveAnalysisDescription', 'lvl0db']}
            rules={[
              { required: true, message: ValidationError.Required },
              {
                validator: validateMinValue(0)
              }
            ]}
          >
            <AppInputNumber />
          </AppForm.Item>
        </>
      )}
      {!isDraftMode && octavaDataStatic && (
        <>
          <TextView label='Магнитуда' value={octavaDataStatic.magnitude} />
          <TextView label='Доля октавы' value={octavaDataStatic.octava} />
          {octavaDataStatic.frequencyLow && (
            <TextView
              label='Нижняя граница частоты, Гц'
              value={octavaDataStatic.frequencyLow}
            />
          )}
          {octavaDataStatic.frequencyHigh && (
            <TextView
              label='Верхняя граница частоты, Гц'
              value={octavaDataStatic.frequencyHigh}
            />
          )}
          <TextView
            label='Перекрытие (%)'
            value={Number(octavaDataStatic.overlap) * 100}
          />
          <TextView
            label='Тип усреднения'
            value={octavaDataStatic.averageType}
          />
          <TextView
            label='Кол-во усреднений'
            value={octavaDataStatic.averageNumber}
          />
          <TextView label='Кол-во полос' value={octavaDataStatic.lanesCount} />
          <TextView
            label='Уровень 0 дБ'
            value={formatRealNumber(octavaDataStatic.lvl0Db, 6)}
          />
        </>
      )}
    </>
  )
}
